import React from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import { phonePrint } from '@pv/common/utils';
import { ChevronDown } from 'react-feather';
import { PLANNER_FRAUD_ANALYSIS_QUERY } from './graphql';
import {
  PlannerFraudAnalysisQuery,
  PlannerFraudAnalysisQueryVariables,
} from '@pv/common/graphql/operations';
import { FraudulentEventCard } from './FraudulentEventDetailCard';

interface SuspiciousMessageReportDetailsDialogProps {
  open: boolean;
  plannerId: string | null;
  onClose: () => void;
}

export const SuspiciousMessageReportDetailsDialog: React.FC<
  SuspiciousMessageReportDetailsDialogProps
> = ({ open, plannerId, onClose }) => {
  const { data, loading } = useQuery<
    PlannerFraudAnalysisQuery,
    PlannerFraudAnalysisQueryVariables
  >(PLANNER_FRAUD_ANALYSIS_QUERY, {
    variables: { plannerId: plannerId || '' },
    skip: !plannerId,
  });

  const { planner, eventsWithMatchingEmail, eventsWithMatchingPhone } =
    data?.plannerFraudAnalysis || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Planner Fraud Analysis
        {planner && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {planner.fullName} - {planner.email}
              {planner.phone && <> | {phonePrint(planner.phone)}</>}
            </Typography>
          </Box>
        )}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <Typography>Loading...</Typography>
          </Box>
        )}

        {!loading && (
          <Box>
            <RelevantEventsList
              events={eventsWithMatchingEmail || []}
              title="Events with the same email"
            />
            <RelevantEventsList
              events={eventsWithMatchingPhone || []}
              title="Events with the same phone number"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="primary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

type Event =
  PlannerFraudAnalysisQuery['plannerFraudAnalysis']['eventsWithMatchingEmail'][0];
const RelevantEventsList = ({
  events,
  title,
}: {
  events: Event[];
  title: string;
}) => {
  const lostAndDeletedEvents = events.filter(
    (event) => event.status === 'lost' || event.discardedAt
  );
  return (
    <Accordion
      slotProps={{ transition: { unmountOnExit: true } }}
      sx={{ border: '1px solid #e0e0e0', borderRadius: 1 }}
    >
      <AccordionSummary expandIcon={<ChevronDown />}>
        <Typography>
          {events?.length || 0} {title}, of which{' '}
          {lostAndDeletedEvents?.length || 0} are lost or deleted
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {events.map((event) => (
            <FraudulentEventCard key={event.id} event={event} />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
